import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { PATH_APP } from "routes/paths";

import { useWorkflowExpressState } from ".";

interface WorkflowExpressProposalMiddlewareProps {
  children: ReactNode;
}

export default function WorkflowExpressProposalMiddleware({
  children,
}: WorkflowExpressProposalMiddlewareProps) {
  const navigate = useNavigate();
  const {
    proposal,
    routesMatcher,
    replacePathWithParams,
    ruralActivity,
    partners,
    warranties,
  } = useWorkflowExpressState();

  const [isLoaded, setIsLoaded] = useState(false);

  const harvestState = useMemo(() => {
    return ruralActivity.data?.harvest?.harvest_states.find(
      (harvestCrop) => harvestCrop.state.id === ruralActivity.data?.state?.id,
    );
  }, [
    ruralActivity.data?.harvest?.harvest_states,
    ruralActivity.data?.state?.id,
  ]);

  const beneficiaryCrop = useMemo(() => {
    return harvestState?.crops.find(
      (crop) => crop.crop_year.toString() === ruralActivity.data?.crop_year,
    );
  }, [harvestState?.crops, ruralActivity.data?.crop_year]);

  const partnersLength = useMemo(() => {
    return partners.data?.length || 0;
  }, [partners.data?.length]);

  const endorserLength = useMemo(() => {
    return warranties.data?.length || 0;
  }, [warranties.data?.length]);

  const handleProposalStartFrom = useCallback(() => {
    const progress = proposal.data?.progressable;

    if (
      !progress?.steps_done.includes("about_financing") ||
      !progress?.steps_done.includes("source_of_income") ||
      !beneficiaryCrop?.total_area ||
      !ruralActivity.data?.start_harvest ||
      !beneficiaryCrop?.progressable.is_done ||
      !beneficiaryCrop.total_area
    ) {
      navigate(
        replacePathWithParams(PATH_APP.workflowExpressProposal.financing),
      );
      setIsLoaded(true);
      return;
    }

    if (
      partners.data?.length === 0 ||
      warranties.data?.length === 0 ||
      partners.data?.some(
        (partner) =>
          !partner.progressable.is_done ||
          warranties.data?.some((endorser) => !endorser.progressable.is_done),
      ) ||
      warranties.data?.some((endorser) => !endorser.progressable.is_done) ||
      (endorserLength < 3 && partnersLength === 0)
    ) {
      navigate(replacePathWithParams(PATH_APP.workflowExpressProposal.people));
      setIsLoaded(true);
      return;
    }

    navigate(replacePathWithParams(PATH_APP.workflowExpressProposal.checkout));
    setIsLoaded(true);
  }, [
    proposal.data,
    navigate,
    replacePathWithParams,
    beneficiaryCrop,
    ruralActivity.data?.start_harvest,
    warranties.data,
    partners.data,
    endorserLength,
    partnersLength,
  ]);

  const handleRedirect = useCallback(() => {
    if (proposal.data?.active_state?.step === "CONSOLIDATION") {
      navigate(replacePathWithParams(PATH_APP.workflowExpressContract.client));
      setIsLoaded(true);
      return;
    }

    handleProposalStartFrom();

    if (proposal.data?.active_state?.step !== "DRAFT") {
      navigate(replacePathWithParams(PATH_APP.proposals.details));
      setIsLoaded(true);
      return;
    }

    setIsLoaded(true);
  }, [proposal.data, navigate, replacePathWithParams, handleProposalStartFrom]);

  useEffect(() => {
    if (
      !isLoaded &&
      !routesMatcher.homepage &&
      !proposal.isLoading &&
      process.env.REACT_APP_ENVIRONMENT &&
      ["production"].includes(process.env.REACT_APP_ENVIRONMENT)
    ) {
      handleRedirect();
    } else {
      setIsLoaded(true);
    }
  }, [proposal.isLoading, isLoaded, handleRedirect, routesMatcher]);

  if (!isLoaded) {
    return <p>carregando...</p>;
  }

  return <>{children}</>;
}
